
import { useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { useChatMessages } from '../hooks/useChatMessages';
import { MessageSquare, Lightbulb, Bot } from 'lucide-react';

export function HRChatInterface() {
  const { messages, isLoading, sendMessage } = useChatMessages();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            HR Assistant powered by Claude
          </h1>
          <p className="text-muted-foreground">Get expert guidance on HR-related questions and policies</p>
        </div>
        <Bot className="h-10 w-10 text-primary/70" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2 h-[600px] flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle>HR Assistant</CardTitle>
            <CardDescription>Ask any HR-related questions and get expert guidance from Claude</CardDescription>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4 flex-1 flex flex-col">
            <ScrollArea className="pr-4 flex-1">
              <div className="flex flex-col gap-1">
                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
            
            <div className="pt-4">
              <ChatInput onSendMessage={sendMessage} isLoading={isLoading} />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              Suggested Topics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {[
                "What are the best practices for onboarding remote employees?",
                "How do I handle a performance improvement plan?",
                "What strategies can improve workplace diversity and inclusion?",
                "Can you explain FMLA requirements and eligibility?",
                "What should be included in an effective employee handbook?",
                "How do I address workplace conflict between team members?",
                "What are current compensation trends in the tech industry?",
                "How should we implement a hybrid work policy?"
              ].map((topic, index) => (
                <li key={index} className="group">
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start text-left h-auto py-2 px-3" 
                    onClick={() => sendMessage(topic)}
                    disabled={isLoading}
                  >
                    <span className="text-sm">{topic}</span>
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
